import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
} from "react-icons/fi";
import { IoCalendarSharp } from "react-icons/io5";
import { useSelector } from "react-redux";

import { DynamicIcon } from "../../../../components/DynamicIcon";

/* ============================================================
   CHECKLIST COLOR MAP
   ============================================================ */

const checklistColorMap = {
  blue: {
    iconBg: "bg-blue-600",
    iconText: "text-white",
    titleText: "text-blue-700",
    border: "border-blue-100",
  },

  slate: {
    iconBg: "bg-slate-600",
    iconText: "text-white",
    titleText: "text-slate-700",
    border: "border-slate-100",
  },

  orange: {
    iconBg: "bg-orange-600",
    iconText: "text-white",
    titleText: "text-orange-700",
    border: "border-orange-100",
  },

  cyan: {
    iconBg: "bg-cyan-600",
    iconText: "text-white",
    titleText: "text-cyan-700",
    border: "border-cyan-100",
  },

  violet: {
    iconBg: "bg-violet-600",
    iconText: "text-white",
    titleText: "text-violet-700",
    border: "border-violet-100",
  },

  indigo: {
    iconBg: "bg-indigo-600",
    iconText: "text-white",
    titleText: "text-indigo-700",
    border: "border-indigo-100",
  },

  red: {
    iconBg: "bg-red-600",
    iconText: "text-white",
    titleText: "text-red-700",
    border: "border-red-100",
  },

  emerald: {
    iconBg: "bg-emerald-600",
    iconText: "text-white",
    titleText: "text-emerald-700",
    border: "border-emerald-100",
  },

  amber: {
    iconBg: "bg-amber-600",
    iconText: "text-white",
    titleText: "text-amber-700",
    border: "border-amber-100",
  },

  yellow: {
    iconBg: "bg-yellow-500",
    iconText: "text-white",
    titleText: "text-yellow-700",
    border: "border-yellow-100",
  },

  pink: {
    iconBg: "bg-pink-600",
    iconText: "text-white",
    titleText: "text-pink-700",
    border: "border-pink-100",
  },

  purple: {
    iconBg: "bg-purple-600",
    iconText: "text-white",
    titleText: "text-purple-700",
    border: "border-purple-100",
  },
};

/* ============================================================
   DEFAULT COLOR
   ============================================================ */

const defaultColor = {
  iconBg: "bg-indigo-600",
  iconText: "text-white",
  titleText: "text-indigo-700",
  border: "border-indigo-100",
};

/* ============================================================
   COMPONENT
   ============================================================ */

const OpeningCheckHeader = ({
  slug,
  selectedDate,
  setSelectedDate,
}) => {
  const navigate = useNavigate();
  const dateInputRef = useRef(null);

  /* ============================================================
     GET CHECKLIST FROM REDUX
     ============================================================ */

  const checkList = useSelector(
    (state) =>
      state.checks.checklistsBySlug?.[slug] || {}
  );

  /* ============================================================
     CHECKLIST DATA
     ============================================================ */

  const checklistTitle =
    checkList?.name || "Checklist";

  const checklistIcon =
    checkList?.icon || "FiCheckCircle";

  const checklistColor =
    checkList?.color || "indigo";

  /* ============================================================
     COLOR CONFIG
     ============================================================ */

  const colorConfig =
    checklistColorMap[checklistColor] ||
    defaultColor;

  /* ============================================================
     FORMATTED DATE
     ============================================================ */

  const formattedDate = selectedDate
    ? new Date(
        `${selectedDate}T00:00:00`
      ).toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  /* ============================================================
     OPEN DATE PICKER
     ============================================================ */

  const openDatePicker = () => {
    if (dateInputRef.current?.showPicker) {
      dateInputRef.current.showPicker();
    } else {
      dateInputRef.current?.click();
    }
  };

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <header className="w-full bg-white py-4 px-6 border-b border-slate-100 flex flex-col gap-3">

      {/* ======================================================
          BREADCRUMB
      ======================================================= */}

      <div className="flex items-center gap-1.5 text-xs text-slate-400">

        <div
          onClick={() => navigate("/checks")}
          className="flex items-center gap-1 text-indigo-600 cursor-pointer hover:text-indigo-700 transition-colors"
        >
          <FiChevronLeft />

          <span>
            Checks
          </span>
        </div>

        <span>
          ›
        </span>

        <span className="text-slate-700">
          {checklistTitle}
        </span>

      </div>

      {/* ======================================================
          HEADER CONTENT
      ======================================================= */}

      <div className="flex justify-between items-center gap-4">

        {/* ====================================================
            TITLE + ICON
        ===================================================== */}

        <div className="flex gap-4 items-center min-w-0">

          {/* DYNAMIC ICON */}

          <div
            className={`
              w-14 h-14
              rounded-xl
              flex items-center justify-center
              shrink-0
              ${colorConfig.iconBg}
            `}
          >
            <DynamicIcon
              iconName={checklistIcon}
              className={`
                w-7 h-7
                ${colorConfig.iconText}
              `}
            />
          </div>

          {/* TITLE */}

          <div className="min-w-0">

            <h1
              className={`
                text-2xl
                font-bold
                text-slate-900
                truncate
              `}
            >
              {checklistTitle}
            </h1>

            <p className="text-xs text-slate-500 mt-1">
              Complete this checklist to confirm the restaurant is ready.
            </p>

          </div>

        </div>

        {/* ====================================================
            DATE
        ===================================================== */}

        <div className="flex gap-2 shrink-0">

          <button
            type="button"
            onClick={openDatePicker}
            className="
              flex
              items-center
              gap-2
              border
              border-slate-200
              rounded-xl
              px-3
              py-2
              bg-white
              hover:bg-slate-50
              transition-colors
              text-sm
            "
          >
            <IoCalendarSharp className="text-slate-600" />

            <span className="text-slate-700">
              {formattedDate}
            </span>
          </button>

          {/* HIDDEN DATE INPUT */}

          <input
            ref={dateInputRef}
            type="date"
            value={selectedDate}
            onChange={(e) =>
              setSelectedDate(e.target.value)
            }
            className="
              absolute
              opacity-0
              pointer-events-none
            "
          />

        </div>

      </div>

    </header>
  );
};

export default OpeningCheckHeader;